import { api } from "@/lib/apiClient";
import { getStoredAuthUser } from "@/lib/authUser";

export type SymbolPosition = "left" | "right" | "left_with_space" | "right_with_space";

export interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  symbolPosition: SymbolPosition;
  decimalPlaces: number;
  decimalSeparator: string;
  thousandSeparator: string;
  isActive: boolean;
}

export const DEFAULT_PKR_CURRENCY: Currency = {
  id: 1,
  code: "PKR",
  name: "Pakistani Rupee",
  symbol: "Rs.",
  symbolPosition: "left_with_space",
  decimalPlaces: 2,
  decimalSeparator: ".",
  thousandSeparator: ",",
  isActive: true,
};

type ApiCurrency = {
  id: number;
  code: string;
  name: string;
  symbol: string;
  symbol_position: SymbolPosition;
  decimal_places: number;
  decimal_separator: string;
  thousand_separator: string;
  is_active: boolean;
};

export function mapApiCurrency(item: ApiCurrency): Currency {
  return {
    id: Number(item.id),
    code: item.code,
    name: item.name,
    symbol: item.symbol,
    symbolPosition: item.symbol_position || "left_with_space",
    decimalPlaces: item.decimal_places ?? 2,
    decimalSeparator: item.decimal_separator || ".",
    thousandSeparator: item.thousand_separator || ",",
    isActive: Boolean(item.is_active),
  };
}

export const DEFAULT_CURRENCIES: Currency[] = [
  DEFAULT_PKR_CURRENCY,
  { id: 2, code: "USD", name: "US Dollar", symbol: "$", symbolPosition: "left", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 3, code: "EUR", name: "Euro", symbol: "€", symbolPosition: "left", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 4, code: "GBP", name: "British Pound", symbol: "£", symbolPosition: "left", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 5, code: "AED", name: "UAE Dirham", symbol: "AED", symbolPosition: "left_with_space", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 6, code: "SAR", name: "Saudi Riyal", symbol: "SAR", symbolPosition: "left_with_space", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 7, code: "CAD", name: "Canadian Dollar", symbol: "CA$", symbolPosition: "left", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
];

let cachedCurrencies: Currency[] = DEFAULT_CURRENCIES;

export async function fetchCurrencies(): Promise<Currency[]> {
  try {
    const response = await api.get<{ data: ApiCurrency[] }>("/api/v1/currencies");
    const rows = response.data?.data;
    if (Array.isArray(rows) && rows.length > 0) {
      cachedCurrencies = rows.map(mapApiCurrency);
    }
  } catch (error) {
    console.error("Failed to fetch currencies from API", error);
  }
  return cachedCurrencies;
}

export function getCachedCurrencies(): Currency[] {
  return cachedCurrencies;
}

export function getActiveCompanyCurrency(): Currency {
  const user = getStoredAuthUser();
  const c = user?.company_branding?.currency;
  if (c && typeof c === "object" && c.symbol) {
    return c;
  }
  return DEFAULT_PKR_CURRENCY;
}

/**
 * Global Currency Formatting Engine
 * Respects company currency symbol, position (left/right/with_space), and decimal precision.
 */
export function formatCurrency(
  amount: number | null | undefined,
  currencyOverride?: Partial<Currency> | string | null,
): string {
  if (amount == null || !Number.isFinite(amount)) {
    return "—";
  }

  let currencyConfig: Currency = getActiveCompanyCurrency();

  if (typeof currencyOverride === "string" && currencyOverride.trim()) {
    const overrideCode = currencyOverride.trim().toUpperCase();
    if (overrideCode !== "PKR" || currencyConfig.code.toUpperCase() === "PKR") {
      const found = cachedCurrencies.find((c) => c.code.toUpperCase() === overrideCode);
      if (found) {
        currencyConfig = found;
      } else {
        currencyConfig = { ...currencyConfig, symbol: currencyOverride.trim(), code: currencyOverride.trim() };
      }
    }
  } else if (currencyOverride && typeof currencyOverride === "object") {
    currencyConfig = { ...currencyConfig, ...currencyOverride };
  }

  const { symbol, symbolPosition, decimalPlaces, decimalSeparator, thousandSeparator } = currencyConfig;

  const num = Math.abs(amount);
  const fixedStr = num.toFixed(decimalPlaces);
  const [integerPart, decimalPart] = fixedStr.split(".");

  // Apply thousand separator
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator || ",");
  const numberFormatted = decimalPlaces > 0 && decimalPart ? `${formattedInteger}${decimalSeparator || "."}${decimalPart}` : formattedInteger;
  const signedNumber = amount < 0 ? `-${numberFormatted}` : numberFormatted;

  switch (symbolPosition) {
    case "left":
      return `${symbol}${signedNumber}`;
    case "right":
      return `${signedNumber}${symbol}`;
    case "right_with_space":
      return `${signedNumber} ${symbol}`;
    case "left_with_space":
    default:
      return `${symbol} ${signedNumber}`;
  }
}
